import React, { useMemo } from "react";
import { formatDate } from "../StudentDashboard/utils";

// Historia podejść jest dostępna z kilku endpointów (własna, rodzica,
// korepetytora i administratora). Starsze endpointy używały nazw pól z
// modelu Mongo, a widok historii używa nazw prezentacyjnych. Normalizujemy
// to w jednym miejscu, aby każdy zapis nadal miał datę i link do snapshotu.
export const normalizeMaturaAttemptRow = (row) => {
  if (!row || typeof row !== "object") return null;

  const snapshotId = row.snapshotId ?? row._id ?? row.id ?? null;
  const points = row.points ?? row.pointsEarned ?? null;
  const maxPoints = row.maxPoints ?? row.maxScore ?? null;
  const explicitPercent = Number(row.percent);
  const score = Number(row.score);
  const calculatedPercent = Number.isFinite(Number(points))
    && Number.isFinite(Number(maxPoints))
    && Number(maxPoints) > 0
    ? Math.round((Number(points) / Number(maxPoints)) * 100)
    : null;

  return {
    ...row,
    snapshotId: snapshotId == null ? null : String(snapshotId),
    finishedAt: row.finishedAt ?? row.createdAt ?? row.updatedAt ?? null,
    points,
    maxPoints,
    percent: Number.isFinite(explicitPercent)
      ? explicitPercent
      : Number.isFinite(score)
        ? Math.round(Math.max(0, Math.min(1, score)) * 100)
        : calculatedPercent ?? 0,
  };
};

export default function MaturaRecentAttemptsTable({
  history,
  loading,
  error,
  onOpenSnapshot,
  maxRecent = 5,
  showBest = true,
  historyHeading = null,
  bestButtonLabel = "Zobacz arkusz",
  emptyMessage = null,
  onToggleStats = null,
}) {
  const { sortedHistory, bestAttemptFromHistory, recentAttempts } = useMemo(() => {
    const arr = Array.isArray(history)
      ? history.map(normalizeMaturaAttemptRow).filter(Boolean)
      : [];

    const toTime = (v) => {
      if (!v) return null;
      const t = new Date(v).getTime();
      return Number.isFinite(t) ? t : null;
    };

    // ✅ sort najstarsze -> najnowsze
    const sorted = arr
      .map((row, inputIdx) => ({ row, inputIdx, t: toTime(row?.finishedAt) }))
      .sort((a, b) => {
        const at = a.t;
        const bt = b.t;
        if (at == null && bt == null) return a.inputIdx - b.inputIdx;
        if (at == null) return 1;
        if (bt == null) return -1;
        if (at !== bt) return at - bt;
        return a.inputIdx - b.inputIdx;
      })
      .map((x) => x.row);

    const counted = sorted.filter((row) => row?.includeInStats !== false);
    const best = counted.reduce(
      (acc, r) => (!acc || r.percent > acc.percent ? r : acc),
      null
    );

    // bierzemy ostatnie maxRecent z końca (najświeższe)
    const slice = sorted.slice(-maxRecent);

    // do renderu tabeli chcemy od najnowszego na górze
    const recent = [...slice].reverse();

    return { sortedHistory: sorted, bestAttemptFromHistory: best, recentAttempts: recent };
  }, [history, maxRecent]);

  const openSnapshot = (snapId) => {
    if (!snapId) return;
    if (typeof onOpenSnapshot === "function") onOpenSnapshot(snapId);
  };

  if (loading) return <div style={{ fontSize: 13, opacity: 0.75 }}>Ładuję historię…</div>;
  if (error) return <div style={{ fontSize: 13, color: "salmon" }}>{error}</div>;

  if (!Array.isArray(history) || !history.length) {
    return (
      <div style={{ fontSize: 13, opacity: 0.78 }}>
        {emptyMessage || "Brak zapisanych podejść."}
      </div>
    );
  }

  // helper: numer podejścia = indeks w posortowanej (najstarsze->najnowsze) + 1
  const getAttemptNo = (row) => {
    const i = sortedHistory.indexOf(row);
    return i >= 0 ? i + 1 : null;
  };

  return (
    <>
      {showBest && bestAttemptFromHistory && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            borderRadius: 14,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(255,255,255,0.04)",
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Najlepsze podejście (z historii)</div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <span>
              Wynik: <b>{bestAttemptFromHistory.percent}%</b> ({bestAttemptFromHistory.points}/
              {bestAttemptFromHistory.maxPoints})
            </span>

            {bestAttemptFromHistory.finishedAt && (
              <span style={{ opacity: 0.85 }}>· {formatDate(bestAttemptFromHistory.finishedAt)}</span>
            )}

            {bestAttemptFromHistory.snapshotId && (
              <button
                type="button"
                onClick={() => openSnapshot(bestAttemptFromHistory.snapshotId)}
                className="btn-secondary"
                style={{ padding: "4px 10px", fontSize: "0.78rem", marginLeft: "auto" }}
              >
                {bestButtonLabel}
              </button>
            )}
          </div>
        </div>
      )}

      <div style={{ fontSize: "0.9rem", fontWeight: 600, marginBottom: 6 }}>
        {historyHeading || `Ostatnie ${recentAttempts.length} podejść`}
      </div>

      <div style={{ width: "100%", overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.86rem" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "6px 4px", borderBottom: "1px solid rgba(255,255,255,0.15)" }}>
                #
              </th>
              <th style={{ textAlign: "left", padding: "6px 4px", borderBottom: "1px solid rgba(255,255,255,0.15)" }}>
                Data
              </th>
              <th style={{ textAlign: "right", padding: "6px 4px", borderBottom: "1px solid rgba(255,255,255,0.15)" }}>
                Wynik
              </th>
              <th style={{ textAlign: "right", padding: "6px 4px", borderBottom: "1px solid rgba(255,255,255,0.15)" }}>
                Punkty
              </th>
              <th style={{ textAlign: "center", padding: "6px 4px", borderBottom: "1px solid rgba(255,255,255,0.15)" }}>
                Podgląd
              </th>
              {onToggleStats ? <th style={{ textAlign: "center", padding: "6px 4px" }}>Statystyki</th> : null}
            </tr>
          </thead>

          <tbody>
            {recentAttempts.map((row) => {
              const no = getAttemptNo(row);
              const isBest =
                bestAttemptFromHistory &&
                row.snapshotId &&
                bestAttemptFromHistory.snapshotId === row.snapshotId;

              return (
                <tr key={row.snapshotId || `${no}-${row.finishedAt || ""}`}>
                  <td style={{ padding: "5px 4px", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
                    {no ?? "—"}
                  </td>

                  <td style={{ padding: "5px 4px", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
                    {row.finishedAt ? formatDate(row.finishedAt) : "—"}
                  </td>

                  <td style={{ padding: "5px 4px", textAlign: "right", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
                    {row.percent}% {isBest ? "⭐" : ""}
                  </td>

                  <td style={{ padding: "5px 4px", textAlign: "right", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
                    {row.points}/{row.maxPoints}
                  </td>

                  <td style={{ padding: "5px 4px", textAlign: "center", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
                    {row.snapshotId ? (
                      <button
                        type="button"
                        onClick={() => openSnapshot(row.snapshotId)}
                        className="btn-secondary"
                        style={{ padding: "4px 10px", fontSize: "0.75rem" }}
                      >
                        Zobacz
                      </button>
                    ) : (
                      "—"
                    )}
                  </td>
                  {onToggleStats ? (
                    <td style={{ padding: "5px 4px", textAlign: "center", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
                      <button
                        type="button"
                        onClick={() => onToggleStats(row)}
                        className="btn-secondary"
                        style={{ padding: "4px 10px", fontSize: "0.75rem" }}
                      >
                        {row.includeInStats === false ? "Uwzględnij" : "Nie uwzględniaj"}
                      </button>
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
